<template>
  <!-- 费用预算：按人编制，部门费用与人员费用是同一份数据的两个视角 -->
  <div class="expense-budget-list">
    <list-page>
      <search-condition @search="loadPage" :show-search="true" page-name="expenseBudgetList">
        <template #default>
          <!--
            维度切换：列表与上方 KPI 都只看当前维度，避免年/季/月三级的金额被加在一起。
            与「费用统计」页的维度切换保持同一套写法。
          -->
          <div class="dim-bar">
            <span class="dim-label">预算维度</span>
            <el-radio-group v-model="query.budgetType" @change="onDimensionChange">
              <el-radio-button :value="1">年预算</el-radio-button>
              <el-radio-button :value="2">季度预算</el-radio-button>
              <el-radio-button :value="3">月预算</el-radio-button>
            </el-radio-group>
          </div>
          <el-select v-model="query.budgetYear" placeholder="全部" clearable @change="loadPage">
            <template #prefix>预算年度</template>
            <el-option v-for="y in yearOptions" :key="y" :label="y" :value="y"/>
          </el-select>
          <!-- 季度只在季度维度下可选；月度维度下选月份即可，季度由月份推得 -->
          <el-select v-if="query.budgetType === 2" v-model="query.budgetQuarter" placeholder="全部" clearable @change="loadPage">
            <template #prefix>季度</template>
            <el-option v-for="q in quarterOptions" :key="q.value" :label="q.label" :value="q.value"/>
          </el-select>
          <el-select v-if="query.budgetType === 3" v-model="query.budgetMonth" placeholder="全部" clearable @change="loadPage">
            <template #prefix>月份</template>
            <el-option v-for="m in monthOptions" :key="m.value" :label="m.label" :value="m.value"/>
          </el-select>
          <depart-select v-model="query.departCode" :data="departTree" placeholder="全部部门" @change="loadPage" :clearable="true">
            <template #prefix>选择部门</template>
          </depart-select>
          <employee-select v-model="query.employeeId">
            <template #prefix>人员</template>
          </employee-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="loadPage">查询</page-button>
          <page-button type="write" el-type="success" @click="openDialog()">+ 新建预算</page-button>
        </template>
      </search-condition>
      <div class="stat-grid">
        <div class="stat-card">
          <div class="label">预算总额</div>
          <div class="value money">{{ money(page.budgetAmount) }}</div>
        </div>
        <div class="stat-card">
          <div class="label">已使用金额</div>
          <div class="value money">{{ money(page.usedAmount) }}</div>
        </div>
        <div class="stat-card">
          <div class="label">可用余额</div>
          <div class="value money" :class="{neg: Number(page.balanceAmount) < 0}">{{ money(page.balanceAmount) }}</div>
        </div>
        <div class="stat-card">
          <div class="label">超支对象</div>
          <div class="value" style="color:#e5484d">{{ page.overCount || 0 }}<span class="unit">个</span></div>
        </div>
      </div>
      <!-- 口径说明：两个视角必然重复计算，不写清楚会被当成 bug -->
      <div class="tip-bar">
        口径说明：同一笔预算会同时计入「所在部门」与「本人」，因此部门费用合计与人员费用合计不相等，
        这是两个视角的差异，不是数据错误。预算按发生日期归年，已使用金额只算已通过的报销单。
      </div>
      <!-- 两张图 -->
      <div class="grid-2">
        <div class="chart-card">
          <div class="section-title">部门预算执行（元）</div>
          <div ref="departChart" class="chart-div"></div>
        </div>
        <div class="chart-card">
          <div class="section-title">人员预算执行率（%）</div>
          <div ref="peopleChart" class="chart-div"></div>
        </div>
      </div>
      <list-table v-loading="loading" background-color="#FFF">
        <!-- 预算明细（原始记录，可编辑删除） -->
        <yq-table :data="dataList" :empty-text="'暂无数据'" height="100%">
          <yq-table-column label="预算编号" prop="budgetNo" width="130"/>
          <yq-table-column label="预算人" width="100">
            <template v-slot="{row}"><span class="cell-title">{{ row.employeeName || '—' }}</span></template>
          </yq-table-column>
          <yq-table-column label="所属部门" prop="departTitle" width="150rem" :show-overflow-tooltip="true"/>
          <yq-table-column label="预算类型" prop="budgetType" align="center" width="90rem">
            <template v-slot="{row}">
              <el-tag size="small" :type="typeTag(row.budgetType)">{{ typeLabel(row.budgetType) }}</el-tag>
            </template>
          </yq-table-column>
          <!-- 预算名称即节点名：2026 / 2026第一季度 / 2026-01，比单看年度更能定位到具体节点 -->
          <yq-table-column label="预算名称" prop="budgetName" min-width="140rem"/>
          <yq-table-column label="预算年度" prop="budgetYear" align="center" width="100rem"/>
          <yq-table-column label="预算金额" prop="budgetAmount" align="right" width="130rem">
            <template v-slot="{row}"><span class="num bold">{{ money(row.budgetAmount) }}</span></template>
          </yq-table-column>
          <yq-table-column label="已使用" prop="usedAmount" align="right" width="130rem">
            <template v-slot="{row}"><span class="num">{{ money(row.usedAmount) }}</span></template>
          </yq-table-column>
          <yq-table-column label="执行进度" prop="bar" min-width="160rem">
            <template v-slot="{row}"><div class="budg-bar" v-html="budgetBar(row)"></div></template>
          </yq-table-column>
          <yq-table-column label="状态" prop="budgetStatus" align="center" width="90rem">
            <template v-slot="{row}">
              <el-tag size="small" :type="statusTagType(row.budgetStatus)">{{ row.budgetStatus }}</el-tag>
            </template>
          </yq-table-column>
          <yq-table-column label="操作" prop="options" align="center" fixed="right" width="130" :show-overflow-tooltip="false">
            <template v-slot="{row}">
              <table-button @click="openDialog(row)">编辑</table-button>
              <table-button @click="deleteRow(row)">删除</table-button>
            </template>
          </yq-table-column>
        </yq-table>
      </list-table>
      <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                 :total-count="searchInfo.totalCount" @onLoad="queryList"/>
    </list-page>

    <!-- 新建/编辑预算 -->
    <el-dialog v-model="showDialog" :title="dialogData.id ? '编辑预算' : '新建预算'" dialog-width="2" :close-on-click-modal="false">
      <form-items :data="dialogData" :columns="2" label-width="3rem" icon-width="0" ref="dialogForm">
        <form-item label="预算人" :required="true" name="employeeId" error-message="请选择预算人">
          <employee-select v-model="dialogData.employeeId" placeholder="请选择预算人，部门自动带出"
                           @change="onEmployeeChange"/>
        </form-item>
        <form-item label="所属部门">
          <!-- 部门由预算人的主部门自动带出，只读展示 -->
          <el-input :model-value="dialogData.departName" disabled placeholder="选择预算人后自动带出"/>
        </form-item>
        <!-- 预算类型只能下拉选，不提供手工输入：节点名要按固定格式拼，手输容易拼错而建出孤立的节点 -->
        <form-item label="预算类型" :required="true" name="budgetType" error-message="请选择预算类型">
          <el-select v-model="dialogData.budgetType" placeholder="请选择" style="width: 100%" @change="onTypeChange">
            <el-option :value="1" label="按年"/>
            <el-option :value="2" label="按季度"/>
            <el-option :value="3" label="按月"/>
          </el-select>
        </form-item>
        <form-item label="预算年度" :required="true" name="budgetYear" error-message="请选择预算年度">
          <el-select v-model="dialogData.budgetYear" placeholder="请选择" style="width: 100%">
            <el-option v-for="y in yearOptions" :key="y" :label="y" :value="y"/>
          </el-select>
        </form-item>
        <form-item v-if="dialogData.budgetType === 2" label="预算季度" :required="true"
                   name="budgetQuarter" error-message="请选择预算季度">
          <el-select v-model="dialogData.budgetQuarter" placeholder="请选择" style="width: 100%">
            <el-option v-for="q in quarterOptions" :key="q.value" :label="q.label" :value="q.value"/>
          </el-select>
        </form-item>
        <form-item v-if="dialogData.budgetType === 3" label="预算月份" :required="true"
                   name="budgetMonth" error-message="请选择预算月份">
          <el-select v-model="dialogData.budgetMonth" placeholder="请选择" style="width: 100%">
            <el-option v-for="m in monthOptions" :key="m.value" :label="m.label" :value="m.value"/>
          </el-select>
        </form-item>
        <form-item label="预算金额(元)" :required="true" name="budgetAmount" error-message="请填写预算金额">
          <el-input-number v-model="dialogData.budgetAmount" :min="0" :precision="2" :controls="false" style="width: 100%"/>
        </form-item>
        <form-item label="备注" :show-block="true">
          <el-input v-model="dialogData.remark" type="textarea" :rows="2" placeholder="选填"/>
        </form-item>
      </form-items>
      <template #footer>
        <page-button @click="showDialog = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveData">保存</page-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import DepartSelect from "@/components/h5/data/DepartSelect.vue";
import expenseBudgetApi from "@/api/dz-crm/expense/expenseBudgetApi";
import commonApi from "@/api/commonApi";
import {useRoute, useRouter} from "vue-router";
import YqTable from "@/components/h5/table/YqTable.vue";

const STATUS_TAG = {"正常": "", "预警": "warning", "超支": "danger"};

/** 预算类型，与后端 BudgetNode.TYPE_* 一一对应 */
const TYPE_LABEL = {1: "年度", 2: "季度", 3: "月度"};
const TYPE_TAG = {1: "primary", 2: "success", 3: "info"};

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "ExpenseBudgetList",
  components: {
    YqTable,
    ListPage, ListTable, SearchCondition, FormPage, FormItems, FormItem,
    PageButton, TableButton, EmployeeSelect, DepartSelect,
  },
  data() {
    const thisYear = new Date().getFullYear();
    return {
      loading: false,
      // budgetType：1年 2季度 3月，默认看年预算（层级最高，数最少最直观）
      query: {searchKey: "", budgetYear: thisYear, departCode: "", budgetType: 1, budgetQuarter: null, budgetMonth: null},
      searchInfo: {pageNumber: 1, pageSize: 20},
      dataList: [],
      page: {},
      departTree: [],
      yearOptions: [thisYear + 1, thisYear, thisYear - 1],
      quarterOptions: [
        {value: 1, label: "第一季度"}, {value: 2, label: "第二季度"},
        {value: 3, label: "第三季度"}, {value: 4, label: "第四季度"},
      ],
      monthOptions: Array.from({length: 12}, (_, i) => ({value: i + 1, label: `${i + 1}月`})),
      showDialog: false,
      dialogData: {},
      departChart: null,
      peopleChart: null,
    };
  },
  computed: {

  },
  beforeRouteLeave(to, from, next) {
    // Util.cachePageData(this, to, from);
    next();
  },
  async mounted() {
    commonApi.queryDepartList().then(list => this.departTree = list || []);
    this.queryList();
    this.loadPage();
  },
  unmounted() {
    [this.departChart, this.peopleChart].forEach(c => c && c.dispose());
  },
  methods: {
    async queryList() {
      this.loading = true;
      try {
        const params = Object.assign({}, this.cleanQuery(this.query), {
          pageNumber: this.searchInfo.pageNumber, pageSize: this.searchInfo.pageSize,
        });
        const {records, totalCount} = await expenseBudgetApi.queryList(params);
        this.dataList = records || [];
        this.searchInfo.totalCount = totalCount;
      } finally {
        this.loading = false;
      }
    },
    async loadPage() {
      this.page = await expenseBudgetApi.page(this.cleanQuery(this.query)) || {};
      this.searchInfo.pageNumber = 1;
      this.queryList();
      this.$nextTick(() => this.renderCharts());
    },
    /** 空值不提交，避免空串绑到后端 */
    cleanQuery(source) {
      const params = {};
      Object.keys(source).forEach(k => {
        const v = source[k];
        if (v === "" || v === null || v === undefined) {
          return;
        }
        params[k] = v;
      });
      return params;
    },
    renderCharts() {
      const departRows = this.page.departRows || [];
      const peopleRows = this.page.peopleRows || [];

      this.departChart = this.renderGroupBar(this.departChart, this.$refs.departChart,
          departRows.map(r => r.dimTitle || r.dimKey || "未分配"),
          [
            {name: "预算金额", data: departRows.map(r => Number(r.budgetAmount || 0)), color: "#1677ff"},
            {name: "已使用", data: departRows.map(r => Number(r.usedAmount || 0)), color: "#faad14"},
          ]);
      // 人员执行率横条，超支红、预警橙、正常蓝。
      // ★ 类目必须兜底成「未知」：dimTitle 为空时 ECharts 的 yAxis 类目是空串，
      // 柱体画不出来，图会整个空掉。
      this.peopleChart = this.renderRateBar(this.peopleChart, this.$refs.peopleChart,
          peopleRows.map(r => r.dimTitle || r.dimKey || "未知"),
          peopleRows.map(r => ({value: Number(r.usedRate || 0), status: r.budgetStatus})));
    },
    /** 分组柱图：预算金额 vs 已使用 */
    renderGroupBar(chart, dom, names, series) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true, axisPointer: {type: "shadow"}},
        legend: {bottom: 0, data: series.map(s => s.name)},
        grid: {left: "0", right: "0", bottom: "12%", top: "10%", containLabel: true},
        xAxis: {type: "category", data: names, axisLabel: {color: "#909399", interval: 0}},
        yAxis: {type: "value", splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}},
        series: series.map(s => ({
          name: s.name, type: "bar", barWidth: "32%",
          itemStyle: {color: s.color}, data: s.data,
        })),
      }, true);
      return chart;
    },
    /** 执行率横条，按状态着色 */
    renderRateBar(chart, dom, names, items) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      const colorOf = (status) => status === "超支" ? "#e5484d" : status === "预警" ? "#f09819" : "#1677ff";
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true, axisPointer: {type: "shadow"}, formatter: "{b}：{c}%"},
        grid: {left: "0", right: "10%", bottom: "3%", top: "5%", containLabel: true},
        xAxis: {type: "value", splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399", formatter: "{value}%"}},
        yAxis: {type: "category", data: names, axisLabel: {color: "#909399", interval: 0}},
        series: [{
          type: "bar", barWidth: "50%",
          data: items.map(it => ({value: it.value, itemStyle: {color: colorOf(it.status)}})),
        }],
      }, true);
      return chart;
    },
    /** 预算执行进度条，内联成 HTML 避免多写一层组件 */
    budgetBar(row) {
      const rate = Number(row.usedRate || 0);
      const clamped = Math.max(0, Math.min(100, rate));
      const cls = rate > 100 ? "over" : rate >= 85 ? "warn" : "";
      return '<div class="bar-wrap"><span class="bar-track"><i class="' + cls +
          '" style="width:' + clamped.toFixed(1) + '%"></i></span>' +
          '<span class="bar-pct">' + rate.toFixed(1) + '%</span></div>';
    },
    statusTagType(status) {
      const type = STATUS_TAG[status];
      return type === "" ? undefined : type;
    },
    /**
     * 选预算人后按 id 查出其主部门，只用于界面回显。
     * ★ 不把部门提交给后端 —— 服务端以职员的实际 mainDepartCode 为准，
     * 避免传入不一致的值导致预算归集错部门。
     */
    onEmployeeChange(employee) {
      if (!employee) {
        return;
      }
      // 主部门从 departList 里取（departType === 1）。
      // ★ 这里不能改用 employee.mainDepartCode：职员选择弹窗的数据来自
      //   commonApi.queryEmployeeListByPage，它走 EmployeeDaoExt.queryList，
      //   只返回 base_column_list，不含 mainDepartCode —— 换了会取到 undefined。
      //   主部门只挂在后端 setDepartListData 装配的 departList 上。
      const departList = employee.departList || [];
      for (let i = 0; i < departList.length; i++) {
        const depart = departList[i];
        if (depart.departType === 1) {
          this.dialogData.departName = depart.departName;
          this.dialogData.departCode = depart.departCode;
          return;
        }
      }
      // 没有标记主部门时退而取第一个，避免选了人却什么都不显示
      if (departList.length) {
        this.dialogData.departName = departList[0].departName;
        this.dialogData.departCode = departList[0].departCode;
      }
    },
    openDialog(row) {
      if (row) {
        // 人员费用列表的聚合行没有 id，不能直接编辑，提示去明细里操作
        if (!row.id) {
          this.$message.warning("这是聚合行，请在下方「预算明细」里编辑具体预算");
          return;
        }
        this.dialogData = JSON.parse(JSON.stringify(row));
      } else {
        // 类型默认跟随当前查询的维度：多数情况是「在看什么层级的列表就建什么层级的预算」
        this.dialogData = {
          budgetType: this.query.budgetType || 1,
          budgetYear: new Date().getFullYear(),
          budgetQuarter: null,
          budgetMonth: null,
        };
      }
      this.showDialog = true;
    },
    /** 切换预算类型：清掉另一层级的定位字段，避免把上个月的残留值一起提交 */
    onTypeChange(type) {
      if (type !== 2) {
        this.dialogData.budgetQuarter = null;
      }
      if (type !== 3) {
        this.dialogData.budgetMonth = null;
      }
    },
    /** 切换查询维度：季度/月份筛选只属于各自的维度，换维度时要清掉，否则会带上无效条件 */
    onDimensionChange() {
      this.query.budgetQuarter = null;
      this.query.budgetMonth = null;
      this.loadPage();
    },
    typeLabel(type) {
      return TYPE_LABEL[type] || "—";
    },
    typeTag(type) {
      return TYPE_TAG[type];
    },
    async saveData() {
      if (!this.$refs.dialogForm.validCheck()) {
        return;
      }
      await expenseBudgetApi.saveBudget(this.dialogData);
      this.showDialog = false;
      this.loadPage();
    },
    deleteRow(row) {
      this.$confirm("确认删除这条预算？", "提示", {type: "warning"})
          .then(() => expenseBudgetApi.delete(row.id))
          .then(() => this.loadPage())
          .catch(() => {});
    },
    money(value) {
      if (value === null || value === undefined) {
        return "0.00";
      }
      return Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    /** 部门费用合计 */
    departSummary({columns, data}) {
      return columns.map((col, i) => {
        if (i === 0) {
          return "合计";
        }
        if (col.label === "预算金额") {
          return this.money(data.reduce((s, r) => s + Number(r.budgetAmount || 0), 0));
        }
        if (col.label === "已使用") {
          return this.money(data.reduce((s, r) => s + Number(r.usedAmount || 0), 0));
        }
        if (col.label === "可用余额") {
          return this.money(data.reduce((s, r) => s + Number(r.balanceAmount || 0), 0));
        }
        return "";
      });
    },
    /** 人员费用合计 */
    peopleSummary({columns, data}) {
      return this.departSummary({columns, data});
    },
  },
};
</script>

<style lang="scss" scoped>
.budget-body {
  height: 100%;
}

/* 维度切换：与「费用统计」页保持同一套观感 */
.dim-bar {
  display: flex;
  align-items: center;
  width: 320px;
  .dim-label {
    font-size: 12px;
    color: #909399;
    margin-right: 8px;
    white-space: nowrap;
  }
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 14px;

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;

    .label {
      font-size: 12px;
      color: #909399;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      height: 34px;
      line-height: 34px;
      margin: 4px 0;

      &.money {
        font-size: 20px;
      }

      &.neg {
        color: #e5484d;
      }

      .unit {
        font-size: 14px;
        font-weight: 400;
        margin-left: 2px;
      }
    }
  }
}

.tip-bar {
  font-size: 12px;
  color: #909399;
  line-height: 1.7;
  background: #fafbfc;
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.chart-card {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;

    .section-sub {
      font-size: 12px;
      font-weight: 400;
      color: #909399;
      margin-left: 8px;
    }
  }

  .chart-div {
    height: 280px;
    width: 100%;
  }
}

/* 执行进度条：内联 HTML 渲染，样式要穿透 scoped */
.budg-bar {
  :deep(.bar-wrap) {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  :deep(.bar-track) {
    flex: 1;
    height: 6px;
    background: #f0f2f5;
    border-radius: 3px;
    overflow: hidden;
    display: block;
  }

  :deep(.bar-track i) {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: #1677ff;
    transition: width .3s;

    &.warn {
      background: #f09819;
    }

    &.over {
      background: #e5484d;
    }
  }

  :deep(.bar-pct) {
    flex: 0 0 52px;
    text-align: right;
    color: #606266;
    font-variant-numeric: tabular-nums;
  }
}


/* ==========================================================================
   轻量表格：去掉全框线，用表头底色 + 行分隔线 + 悬停高亮
   三个费用页面共用同一套观感（各自 scoped，这里各写一份）
   ========================================================================== */
.clean-table {
  /* 去掉 el-table 底部那条 1px 线 */
  :deep(.el-table__inner-wrapper::before) {
    display: none;
  }

  /* 表头：浅底、无边框、字号略小 */
  :deep(th.el-table__cell) {
    background: #fafbfc;
    border: none;
    border-bottom: 1px solid var(--yq-border-color, #ebeef5);
    color: #606266;
    font-weight: 600;
    font-size: 12px;
    padding: 9px 0;
  }

  /* 单元格：只留行底细线，去掉竖线 */
  :deep(td.el-table__cell) {
    border: none;
    border-bottom: 1px solid #f5f6f8;
    padding: 11px 0;
    color: #303133;
  }

  :deep(.el-table__row:hover > td.el-table__cell) {
    background: #f5f9ff;
  }

  /* 合计行 */
  :deep(.el-table__footer-wrapper td.el-table__cell) {
    background: #fafbfc;
    border-top: 1px solid var(--yq-border-color, #ebeef5);
    border-bottom: none;
    font-weight: 600;
    color: #303133;
  }

  /* 斑马纹弱化，与行分隔线不要打架 */
  :deep(.el-table__row--striped td.el-table__cell) {
    background: #fcfcfd;
  }

  /* 单元格内的主色标题，让每行的识别点更清楚 */
  .cell-title {
    color: #303133;
    font-weight: 500;
  }
}

.num {
  font-variant-numeric: tabular-nums;
}

.neg {
  color: #e5484d;
}

.bold {
  font-weight: 600;
  color: #303133;
}

</style>
